import { useEffect } from 'react';

// Canonical site URL for production
const SITE_URL = 'https://www.flightcents.com';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  schema?: any;
  canonicalUrl?: string;
  imageUrl?: string;
  noindex?: boolean;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title = "Flight Training Cost Tracker - FlightCents | Track Flight School Spending & Aviation Budget",
  description = "Best flight training cost tracker and pilot expense app. Track flight school spending, compare costs vs Flight Schedule Pro & ForeFlight. Free aviation budgeting tool trusted by student pilots. Calculate true PPL costs with real-time budget tracking.",
  keywords = "flight training cost tracker, pilot expense app, aviation budgeting tool, track flight school spending, compare pilot budgeting apps, flight training budget calculator, student pilot cost tracking, PPL cost tracker, aviation expense management, pilot training budget app",
  schema,
  canonicalUrl,
  imageUrl,
  noindex = false,
}) => {
  useEffect(() => {
    // Update title
    document.title = title;

    // Update meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('robots', noindex ? 'noindex, nofollow' : 'index, follow');
    
    // Canonical link
    if (canonicalUrl) updateLinkTag('canonical', canonicalUrl);
    
    // Open Graph tags for social sharing
    updateMetaTag('og:title', title, 'property');
    updateMetaTag('og:description', description, 'property');
    updateMetaTag('og:type', 'website', 'property');
    updateMetaTag('og:site_name', 'FlightCents', 'property');
    if (canonicalUrl) updateMetaTag('og:url', canonicalUrl, 'property');
    if (imageUrl) updateMetaTag('og:image', imageUrl, 'property');
    
    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image', 'name');
    updateMetaTag('twitter:title', title, 'name');
    updateMetaTag('twitter:description', description, 'name');
    if (canonicalUrl) updateMetaTag('twitter:url', canonicalUrl, 'name');
    if (imageUrl) updateMetaTag('twitter:image', imageUrl, 'name');

    // Mobile optimization
    updateMetaTag('viewport', 'width=device-width, initial-scale=1, maximum-scale=5', 'name');
    updateMetaTag('theme-color', '#334155', 'name');

    // Schema.org JSON-LD
    if (schema) {
      const existingScript = document.getElementById('schema-jsonld');
      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement('script');
      script.id = 'schema-jsonld';
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, schema, canonicalUrl, imageUrl, noindex]);

  return null;
};

const updateMetaTag = (name: string, content: string, attribute: 'name' | 'property' = 'name') => {
  let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
  
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }
  
  element.content = content;
};

const updateLinkTag = (rel: string, href: string) => {
  let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

export const getProductSchema = () => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "FlightCents - Flight Training Cost Tracker",
  "applicationCategory": "FinanceApplication",
  "operatingSystem": "Web, iOS, Android",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "ratingCount": "347",
    "bestRating": "5",
    "worstRating": "1"
  },
  "description": "FlightCents is the best flight training cost tracker and pilot expense app for student pilots. Track flight school spending in real-time, monitor aviation training budgets, and calculate true PPL costs. Unlike Flight Schedule Pro and ForeFlight, FlightCents focuses exclusively on cost transparency and budget management.",
  "featureList": [
    "Real-time flight training expense tracking",
    "Aviation budget calculator with PPL cost estimates",
    "Comprehensive expense categories for pilot training",
    "Flight hours and cost per hour analytics",
    "Training milestone tracking with cost breakdowns",
    "Export reports for tax deductions and financial planning",
    "Mobile-first responsive design for pilots on the go",
    "Secure local data storage with privacy protection"
  ],
  "creator": {
    "@type": "Organization",
    "name": "FlightCents",
    "description": "Aviation budget management solutions for student pilots"
  }
});

export const getOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "FlightCents",
  "description": "Free flight training cost tracker and aviation budgeting tool",
  "url": SITE_URL,
  "logo": SITE_URL + "/icons/flightcents.png",
  "sameAs": [
    "https://www.reddit.com/r/flying",
    "https://www.reddit.com/r/studentpilot"
  ]
});

export const getFAQSchema = (faqs: Array<{question: string; answer: string}>) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

export default SEOHead;
